import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, PhoneIcon, ChevronDownIcon, ChevronRightIcon, GlobeIcon, ShieldCheckIcon, HardDriveIcon, LayoutIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Navbar({
  onNavigate,
  currentPage,
  onOpenQuote
}: {
  onNavigate: (page: string) => void;
  currentPage: string;
  onOpenQuote?: () => void;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('internet');
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNav = (page: string) => {
    onNavigate(page);
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  };
  const linkClass = (page?: string) => `font-medium transition-colors cursor-pointer ${page && currentPage === page ? 'text-accent' : 'text-slate-600 hover:text-accent'}`;
  const categories = [{
    id: 'internet',
    label: 'Business Internet',
    icon: GlobeIcon,
    items: [{
      name: 'Broadband Internet',
      id: 'business-internet'
    }, {
      name: 'Ethernet and Fiber Internet',
      id: 'ethernet-fiber'
    }, {
      name: '5G Wireless Internet',
      id: 'wireless-5g'
    }]
  }, {
    id: 'phone',
    label: 'Business Phone',
    icon: PhoneIcon,
    items: [{
      name: 'UCaaS Phone System',
      id: 'ucaas'
    }, {
      name: 'Voice Over IP Phone System',
      id: 'business-phone'
    }, {
      name: 'Wireless Phone',
      id: 'wireless-phone'
    }]
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldCheckIcon,
    items: [{
      name: 'Video & Monitoring',
      id: 'video-monitoring'
    }, {
      name: 'Access Control',
      id: 'access-control'
    }]
  }, {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: HardDriveIcon,
    items: [{
      name: 'Ethernet Cabling',
      id: 'ethernet-cabling'
    }]
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-5'}`} data-id="element-240">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-241">
        <div className="flex justify-between items-center" data-id="element-242">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center" data-id="element-243">
            <button onClick={() => handleNav('home')} className="block" data-id="element-244">
              <img src="https://cdn.magicpatterns.com/uploads/fRhFPj1omhhVd8L4VTw3h8/ChatGPT_Image_Apr_29,_2026,_09_10_58_PM.png" alt="Ambient Telecom Solutions" className="h-20 w-auto" data-id="element-245" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8 items-center" data-id="element-246">
            <button onClick={() => handleNav('about')} className={linkClass('about')} data-id="element-247">
              About Us
            </button>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesDropdownOpen(true)} onMouseLeave={() => setServicesDropdownOpen(false)} data-id="element-248">
              <button className={`flex items-center hover:text-accent font-medium transition-colors py-2 ${['business-internet', 'ethernet-fiber', 'ucaas', 'business-phone', 'video-monitoring', 'access-control', 'ethernet-cabling', 'website-design'].includes(currentPage) ? 'text-accent' : 'text-slate-600'}`} data-id="element-249">
                Services <ChevronRightIcon className="ml-1 w-4 h-4" data-id="element-250" />
              </button>

              <AnimatePresence data-id="element-251">
                {servicesDropdownOpen && <motion.div initial={{
                opacity: 0,
                x: 10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                x: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                x: 10,
                scale: 0.95
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="absolute top-0 left-full ml-4 w-[540px] bg-white shadow-2xl rounded-2xl overflow-hidden border border-slate-100 flex" data-id="element-252">
                    {/* Categories Sidebar */}
                    <div className="w-56 bg-slate-50/50 border-r border-slate-100 p-3 space-y-1" data-id="element-253">
                      {categories.map(cat => <button key={cat.id} onMouseEnter={() => setActiveCategory(cat.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${activeCategory === cat.id ? 'bg-white text-accent shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-254">
                          <div className="flex items-center gap-2.5" data-id="element-255">
                            <cat.icon className={`w-4 h-4 ${activeCategory === cat.id ? 'text-accent' : 'text-slate-400'}`} data-id="element-256" />
                            {cat.label}
                          </div>
                          <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform ${activeCategory === cat.id ? 'translate-x-0.5 opacity-100' : 'opacity-0'}`} data-id="element-257" />
                        </button>)}
                      <div className="pt-2 mt-2 border-t border-slate-100" data-id="element-258">
                        <button onClick={() => handleNav('website-design')} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${currentPage === 'website-design' ? 'text-accent bg-accent/5' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-259">
                          <LayoutIcon className="w-4 h-4 text-amber-500" data-id="element-260" />
                          Free Website Design
                        </button>
                      </div>
                    </div>

                    {/* Items Content */}
                    <div className="flex-1 p-6 bg-white" data-id="element-261">
                      <div className="grid grid-cols-1 gap-1" data-id="element-262">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-3" data-id="element-263">
                          {categories.find(c => c.id === activeCategory)?.label}{' '}
                          Solutions
                        </div>
                        {categories.find(c => c.id === activeCategory)?.items.map(item => <button key={item.id} onClick={() => handleNav(item.id)} className={`group w-full text-left px-4 py-3 rounded-xl transition-all hover:bg-slate-50 ${currentPage === item.id ? 'bg-accent/5 text-accent font-semibold' : 'text-slate-700'}`} data-id="element-264">
                              <div className="flex items-center justify-between" data-id="element-265">
                                <span className="text-sm" data-id="element-266">{item.name}</span>
                                <ChevronRightIcon className={`w-4 h-4 transition-all ${currentPage === item.id ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 text-accent'}`} data-id="element-267" />
                              </div>
                            </button>)}
                      </div>

                      {/* Featured Promo in Dropdown */}
                      <div className="mt-6 p-4 bg-primary/5 rounded-2xl border border-primary/10" data-id="element-268">
                        <div className="text-xs font-bold text-primary mb-1" data-id="element-269">
                          Limited Time Offer
                        </div>
                        <div className="text-[11px] text-slate-600 leading-relaxed" data-id="element-270">
                          Get a free bill audit when you switch to any{' '}
                          {categories.find(c => c.id === activeCategory)?.label}{' '}
                          plan today.
                        </div>
                      </div>
                    </div>

                    {/* Hover Bridge */}
                    <div className="absolute -left-4 top-0 w-4 h-full" data-id="element-271" />
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button onClick={() => handleNav('website-design')} className={linkClass('website-design')} data-id="element-272">
              Free Website Design
            </button>

            <button onClick={() => handleNav('suppliers')} className={linkClass('suppliers')} data-id="element-273">
              Suppliers
            </button>
            <button onClick={() => handleNav('contact')} className={linkClass('contact')} data-id="element-274">
              Contact Us
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-275">
            <a href="tel:+4435451747" className="flex items-center text-slate-700 hover:text-accent font-medium transition-colors" data-id="element-276">
              <PhoneIcon className="w-4 h-4 mr-2 text-accent" data-id="element-277" />
              (443) 545-1747
            </a>
            <button onClick={onOpenQuote} className="bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-full font-medium transition-colors shadow-sm" data-id="element-278">
              Get a Quote
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center" data-id="element-279">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-slate-600 hover:text-primary focus:outline-none" aria-label="Toggle menu" data-id="element-280">
              {mobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-281" /> : <MenuIcon className="h-6 w-6" data-id="element-282" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="lg:hidden bg-white border-t border-slate-100 absolute top-full left-0 w-full shadow-lg" data-id="element-283">
          <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-284">
            <button onClick={() => handleNav('about')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'about' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-285">
              About Us
            </button>

            <div className="px-3 py-2" data-id="element-286">
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="w-full flex items-center justify-between text-base font-medium text-slate-900 mb-2 focus:outline-none" data-id="element-287">
                Services
                <ChevronDownIcon className={`w-5 h-5 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-288" />
              </button>
              {mobileServicesOpen && <div className="pl-4 space-y-2 border-l-2 border-slate-100 mt-2" data-id="element-289">
                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-290">
                    Business Internet
                  </div>
                  <button onClick={() => handleNav('business-internet')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'business-internet' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-291">
                    Broadband Internet
                  </button>
                  <button onClick={() => handleNav('ethernet-fiber')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'ethernet-fiber' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-292">
                    Ethernet and Fiber Internet
                  </button>
                  <button onClick={() => handleNav('wireless-5g')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'wireless-5g' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-293">
                    5G Wireless Internet
                  </button>

                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2" data-id="element-294">
                    Business Phone
                  </div>
                  <button onClick={() => handleNav('ucaas')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'ucaas' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-295">
                    UCaaS Phone
                  </button>
                  <button onClick={() => handleNav('business-phone')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'business-phone' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-296">
                    Voice Over IP Phone System
                  </button>
                  <button onClick={() => handleNav('wireless-phone')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'wireless-phone' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-297">
                    Wireless Phone
                  </button>

                  <div className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2" data-id="element-298">
                    Other Services
                  </div>
                  <button onClick={() => handleNav('video-monitoring')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'video-monitoring' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-299">
                    Video & Monitoring
                  </button>
                  <button onClick={() => handleNav('access-control')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'access-control' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-300">
                    Access Control
                  </button>
                  <button onClick={() => handleNav('ethernet-cabling')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'ethernet-cabling' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-301">
                    Ethernet Cabling
                  </button>
                  <button onClick={() => handleNav('website-design')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'website-design' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-302">
                    Free Website Design
                  </button>
                </div>}
            </div>

            <button onClick={() => handleNav('website-design')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'website-design' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-303">
              Free Website Design
            </button>

            <button onClick={() => handleNav('suppliers')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'suppliers' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-304">
              Suppliers
            </button>
            <button onClick={() => handleNav('contact')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'contact' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-305">
              Contact Us
            </button>

            <div className="mt-6 pt-6 border-t border-slate-100 px-3 flex flex-col space-y-4" data-id="element-306">
              <a href="tel:+4435451747" className="flex items-center justify-center text-slate-700 font-medium py-2" data-id="element-307">
                <PhoneIcon className="w-5 h-5 mr-2 text-primary" data-id="element-308" />
                (443) 545-1747
              </a>
              <button onClick={() => {
            onOpenQuote?.();
            setMobileMenuOpen(false);
          }} className="w-full text-center bg-primary text-white px-6 py-3 rounded-full font-medium" data-id="element-309">
                Get a Quote
              </button>
            </div>
          </div>
        </div>}
    </header>;
}